import Link from 'next/link';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Button — primary (ink filled), secondary (line outline), ghost (text only).
 * Touch-min 44px. Renders as <a> if `href`, otherwise <button>.
 *
 * V3.4 (2026-05-29) restyle:
 *   primary    ink filled, paper text, hover ink-soft. Lifts brand
 *              gravity over the old warmth-dark filled CTAs which
 *              now read too 'product-y' against the gold-3 accent.
 *   secondary  white surface + line border, hover gold-3 border.
 *   ghost      transparent, ink-mute text, hover gold-3 text.
 *
 * Radii ≤10px throughout (v3.4 visual rule). Was rounded-full for sm
 * and rounded-2xl for md/lg — both now rounded-r-sm.
 */
type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-ink text-paper hover:bg-ink-soft border border-transparent',
  secondary: 'bg-white text-ink border border-line hover:border-gold-3 hover:text-gold-3',
  ghost: 'bg-transparent text-ink-mute hover:text-gold-3 border border-transparent',
};

const SIZE: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-r-xs min-h-[36px]',
  md: 'px-4 py-2.5 text-base rounded-r-sm min-h-touch',
  lg: 'px-6 py-3 text-base rounded-r-md min-h-touch',
};

type CommonProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
};

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...rest
}: CommonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`${VARIANT[variant]} ${SIZE[size]} font-medium transition inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${className ?? ''}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function LinkButton({
  href,
  variant = 'primary',
  size = 'md',
  className,
  children,
}: CommonProps & { href: string }) {
  return (
    <Link
      href={href}
      className={`${VARIANT[variant]} ${SIZE[size]} font-medium transition inline-flex items-center justify-center gap-2 ${className ?? ''}`}
    >
      {children}
    </Link>
  );
}
